/**
 * Click-to-navigate map for notifications: notification.type -> the page
 * segment that owns it in each application VIEW:
 *
 *   admin    -> Admin View       (/dashboard for admins, /DashboardManager for
 *                                 admin-level managers, AdminRoutes under
 *                                 /userprofile for admin-level users)
 *   manager  -> Manager View     (/DashboardManager)
 *   employee -> User View        (/userprofile, /userprofileadmin, /userprofilemanager)
 *   super    -> SuperAdmin shell (/DashboardSuper)
 *
 * Rows are curated by WHO actually receives each type (see the backend
 * producers in user-service — leaveService, advancementService,
 * escalationService, systemNotificationService, ...). Personal notifications
 * ("your leave was approved", "your salary changed") carry only the
 * `employee` key so every role lands in their own User View; managerial /
 * approval notifications carry `admin`/`manager` keys. The view-aware
 * resolution + shell routing lives in utils/notificationNavigation.js.
 *
 * To route a new notification type, add one row here. If it needs a deep-link
 * param, extend getNotificationTarget below.
 */
export const NOTIFICATION_ROUTES = {
  // Sent to approvers (myAdmin / first-line manager / admin-level users) —
  // never to the requester (they get LEAVE_CONFIRMATION instead).
  LEAVE_REQUEST:          { admin: 'LeaveApprovalPage', manager: 'LeaveApprovalPage' },
  // Personal: requester-only.
  LEAVE_CONFIRMATION:     { employee: 'leave-management' },
  LEAVE_STATUS_UPDATE:    { employee: 'leave-management' },
  LEAVE_APPROVED:         { employee: 'leave-management' },
  LEAVE_REJECTED:         { employee: 'leave-management' },
  MANUAL_LEAVE_ASSIGNED:  { employee: 'leave-management' },

  MESSAGE:                { admin: 'messages', manager: 'messages', employee: 'messages' },
  GROUP_ADDED:            { admin: 'messages', manager: 'messages', employee: 'messages' },

  // Same type covers both directions: "new request to process" (module-access
  // users) and "your document is ready" (the employee). The viewer's role
  // picks the side: admins/managers -> processing page, employees -> their
  // self-service page.
  DOCUMENT_REQUEST:       { admin: 'Mo3tiAdminDocumentRequests', manager: 'Mo3tiAdminDocumentRequests', employee: 'employee-services' },

  // Personal: self-confirmation to the requester when they submit.
  ADVANCEMENT_REQUEST:         { employee: 'employee-services' },
  // To admin-level users / assigned managers / workflow assignees.
  ADVANCEMENT_ACTION_REQUIRED: { admin: 'Mo3tiAdvancementManagement', manager: 'Mo3tiAdvancementManagement' },
  ADVANCEMENT_REQUEST_UPDATED: { admin: 'Mo3tiAdvancementManagement', manager: 'Mo3tiAdvancementManagement' },
  ADVANCEMENT_STATUS_ADMIN:    { admin: 'Mo3tiAdvancementManagement', manager: 'Mo3tiAdvancementManagement' },
  // Personal: status updates to the requesting employee.
  ADVANCEMENT_STATUS:          { employee: 'employee-services' },
  ADVANCEMENT_APPROVED:        { employee: 'employee-services' },
  ADVANCEMENT_REJECTED:        { employee: 'employee-services' },
  ADVANCEMENT_DISBURSED:       { employee: 'employee-services' },

  // To the managers (approval flow) AND to the affected employee (on
  // approval/completion) — the employee sees the result on their own profile.
  EMPLOYEE_ACTION:        { admin: 'ChangesPage', manager: 'ChangesPage', employee: 'professionalProfile' },
  EMPLOYEE_CHANGE:        { admin: 'ChangesPage', manager: 'ChangesPage', employee: 'professionalProfile' },

  CALENDAR_EVENT:         { admin: 'calendar', manager: 'calendar', employee: 'calendar' },

  // The escalation (complaints) page only exists in User View shells — the
  // recipients (submitter / resolver-group members) always review there,
  // whatever their role.
  ESCALATION_SUBMITTED:       { employee: 'escalation' },
  ESCALATION_ASSIGNED:        { employee: 'escalation' },
  ESCALATION_STATUS_UPDATE:   { employee: 'escalation' },
  ESCALATION_REVIEW_REQUIRED: { employee: 'escalation' },

  // Groups Management is likewise User View only.
  GROUP_MEMBER_ADDED:      { employee: 'groups' },

  REPORT_EXPORTED:         { admin: 'reports', manager: 'reports' },
  REPORT_SHARED:           { admin: 'reports', manager: 'reports' },
  REPORT_SCHEDULE_CREATED: { admin: 'reports', manager: 'reports' },
  REPORT_SCHEDULE_RUN:     { admin: 'reports', manager: 'reports' },

  // Workflow approvals: the concrete page is picked per workflow module in
  // getNotificationTarget (LEAVE/DOCUMENT/ACTION/ADVANCEMENT); this row is
  // the fallback when the module ref is missing.
  WORKFLOW_APPROVAL:      { admin: 'ChangesPage', manager: 'ChangesPage' },

  // ── Account / security lifecycle (all personal → User View) ───────────
  WELCOME:            { employee: 'professionalProfile' },
  PASSWORD_CHANGED:   { admin: 'Settings', manager: 'Settings', employee: 'settings', super: 'Settings' },
  ADMIN_LEVEL_GRANTED: { employee: 'professionalProfile' },
  PERMISSION_UPDATED:  { employee: 'professionalProfile' },

  // ── Employee data / payroll / attendance ──────────────────────────────
  // PROFILE_UPDATED goes to the employee (own profile), the acting admin
  // (employees table, auto-open the employee) and superadmins (no matching
  // page in their shell → no-op).
  PROFILE_UPDATED:    { admin: 'employees', manager: 'employees', employee: 'professionalProfile' },
  // Personal: only the affected employee is notified — always their own
  // payslip / attendance page, whatever their role.
  PAYROLL_ADJUSTMENT: { employee: 'salary' },
  ATTENDANCE_RESET:   { employee: 'attendance' },

  // ── Company-wide policy changes (fan-out to affected employees) ────────
  COMPANY_CONFIG_UPDATED: { admin: 'configuration', employee: 'calendar' },
};

/** Resolve the page segment for a notification type + view key, or null. */
export function getNotificationPage(type, role) {
  return NOTIFICATION_ROUTES[type]?.[role] || null;
}

/**
 * Parse the backend's `actionUrl` deep-link descriptor — a plain query-string
 * of entity refs (e.g. "employee=1001&date=2026-07-20") set by the producers
 * (see user-service systemNotificationService.deepLink) — into an object.
 */
export function parseActionUrl(actionUrl) {
  // Ignore full URLs and legacy path-style values (e.g. "/approvals/1/2") —
  // only the producers' query-string descriptors are parseable refs.
  if (!actionUrl || typeof actionUrl !== 'string' || actionUrl.includes('://') || actionUrl.startsWith('/')) return {};
  try {
    return Object.fromEntries(new URLSearchParams(actionUrl));
  } catch (e) {
    return {};
  }
}

/**
 * Resolve the full navigation target for a notification: the page segment
 * per view (admin/manager/employee/super) plus the query params that let the
 * destination page focus the exact record (open its modal / highlight the
 * row / jump to the date). Every param is optional — a page simply ignores
 * params it doesn't understand, so unknown/legacy notifications degrade to
 * plain page navigation.
 *
 * The caller (utils/notificationNavigation.js) picks WHICH view to use based
 * on the viewer's role and current view, and builds the final path.
 *
 * @param {object} notification The notification object.
 * @param {object} [opts] { selfCode } — the viewer's own infinityCode, used
 *   to disambiguate dual-audience types (an EMPLOYEE_ACTION about YOU is
 *   personal; about one of your reports it's managerial).
 * @returns {{ pages: object, query: object }}
 */
export function getNotificationTarget(notification, opts = {}) {
  const type = notification?.type || '';
  const selfCode = opts.selfCode != null ? String(opts.selfCode) : null;
  const meta = parseActionUrl(notification?.actionUrl);
  let pages = { ...(NOTIFICATION_ROUTES[type] || {}) };
  const query = {};

  if (type.startsWith('LEAVE') || type === 'MANUAL_LEAVE_ASSIGNED') {
    if (notification.leaveId) query.leaveId = notification.leaveId;
  } else if (type === 'DOCUMENT_REQUEST') {
    if (meta.documentRequestId) query.documentId = meta.documentRequestId;
  } else if (type.startsWith('ADVANCEMENT')) {
    if (meta.advancementId) query.advancementId = meta.advancementId;
  } else if (type.startsWith('ESCALATION')) {
    if (meta.escalationId) query.escalationId = meta.escalationId;
    // The escalation page has two sections: the submitter's own complaints
    // ("my") and the resolver group's queue ("assigned").
    query.view = type === 'ESCALATION_REVIEW_REQUIRED' ? 'assigned' : 'my';
  } else if (type === 'EMPLOYEE_ACTION' || type === 'EMPLOYEE_CHANGE') {
    if (meta.actionId) query.actionId = meta.actionId;
    // The producer embeds the AFFECTED employee's code. When that's the
    // viewer themselves ("your promotion was approved") it's personal →
    // User View profile; otherwise it's a review notification → ChangesPage.
    if (meta.employee && selfCode) {
      pages = String(meta.employee) === selfCode
        ? { employee: pages.employee }
        : { admin: pages.admin, manager: pages.manager };
    }
  } else if (type === 'PROFILE_UPDATED') {
    // Admin/manager land on the employees table and auto-open that employee.
    // Self-disambiguation: an update about YOUR profile is personal (your own
    // profile page, no param needed), even if you're an admin — another admin
    // edited you.
    if (meta.employee && selfCode && String(meta.employee) === selfCode) {
      pages = { employee: pages.employee };
    } else if (meta.employee) {
      if (selfCode) pages = { admin: pages.admin, manager: pages.manager };
      query.employee = meta.employee;
    }
  } else if (type === 'PAYROLL_ADJUSTMENT') {
    // Personal payslip page — jump straight to the adjusted period.
    if (meta.year) query.year = meta.year;
    if (meta.month) query.month = meta.month;
  } else if (type === 'ATTENDANCE_RESET') {
    // Personal attendance page — highlight the affected day.
    if (meta.date) query.date = meta.date;
  } else if (type === 'CALENDAR_EVENT') {
    if (notification.eventId) query.eventId = notification.eventId;
  } else if (type === 'MESSAGE') {
    // Aggregated chat notification: when exactly ONE person sent messages,
    // open that conversation directly; with several senders there is no
    // single thread to open, so just land on the messenger.
    const senders = Array.isArray(notification.senders) ? notification.senders : [];
    if (senders.length === 1 && senders[0]?.infinityCode) {
      query.chat = senders[0].infinityCode;
    } else if (senders.length === 0 && notification.from && /^\d+$/.test(String(notification.from))) {
      query.chat = notification.from;
    }
  } else if (type === 'WORKFLOW_APPROVAL') {
    // The pending approval lives on a different page per workflow module.
    const modulePages = {
      LEAVE: 'LeaveApprovalPage',
      DOCUMENT: 'Mo3tiAdminDocumentRequests',
      ACTION: 'ChangesPage',
      ADVANCEMENT: 'Mo3tiAdvancementManagement',
    };
    const modulePage = modulePages[String(meta.module || '').toUpperCase()];
    if (modulePage) pages = { admin: modulePage, manager: modulePage };
  } else if (type === 'COMPANY_CONFIG_UPDATED') {
    // Holiday-calendar notifications carry a holidayId. Holiday management
    // lives on the admin leave page (opens the holiday modal); employees see
    // holidays on their own leave page's "holidays" tab. Plain config-change
    // notifications keep the static route (admin → configuration page,
    // employee → calendar).
    if (meta.holidayId) {
      pages = { admin: 'LeaveApprovalPage', manager: 'LeaveApprovalPage', employee: 'leave-management' };
      query.holidayId = meta.holidayId;
      query.tab = 'holidays';
    }
  }

  return { pages, query };
}
